import React, { useState, useEffect, useRef, useCallback } from "react";
import { connectionsGroups } from "../data/connectionsData";
import "./ConnectionsGame.css";

function ConnectionsGame() {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [solvedGroups, setSolvedGroups] = useState([]);
  const [mistakesRemaining, setMistakesRemaining] = useState(4);
  const [message, setMessage] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const audioRef = useRef(null);

  const resetGame = useCallback(() => {
    const allItems = connectionsGroups.flatMap((group) => group.items);
    const shuffled = shuffleArray(allItems);
    setItems(shuffled);
    setSelectedItems([]);
    setSolvedGroups([]);
    setMistakesRemaining(4);
    setMessage("");
    setGameOver(false);
    setGameWon(false);
  }, []);

  // Initialize shuffled items
  useEffect(() => {
    resetGame();
  }, [resetGame]);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleItemClick = (item) => {
    if (gameOver || gameWon) return;

    // Check if item is already solved
    const isSolved = solvedGroups.some((group) => group.items.includes(item));
    if (isSolved) return;

    if (selectedItems.includes(item)) {
      setSelectedItems(selectedItems.filter((i) => i !== item));
    } else if (selectedItems.length < 4) {
      const newSelected = [...selectedItems, item];
      setSelectedItems(newSelected);
    }
  };

  const handleSubmit = () => {
    if (selectedItems.length === 4) {
      checkSubmission(selectedItems);
    }
  };

  const checkSubmission = (selected) => {
    // Find which group matches exactly
    const matchedGroup = connectionsGroups.find((group) => {
      return (
        group.items.length === selected.length &&
        group.items.every((item) => selected.includes(item))
      );
    });

    if (matchedGroup && !solvedGroups.find((g) => g.id === matchedGroup.id)) {
      // Correct match!
      setSolvedGroups([...solvedGroups, matchedGroup]);
      setSelectedItems([]);
      setMessage("Correct! ✨");

      // Check if all groups are solved
      if (solvedGroups.length + 1 === connectionsGroups.length) {
        setGameWon(true);
        setMessage("🎉 You won! Amazing memory! 🎉");
        // Play victory music
        if (audioRef.current) {
          audioRef.current.currentTime = 0; // Reset to beginning
          audioRef.current
            .play()
            .catch((e) => console.log("Audio play failed:", e));
        }
      }

      setTimeout(() => {
        if (solvedGroups.length + 1 !== connectionsGroups.length) {
          setMessage("");
        }
      }, 2000);
    } else {
      // Check if "one away" (3 out of 4 match any group)
      let isOneAway = false;
      for (const group of connectionsGroups) {
        if (solvedGroups.find((g) => g.id === group.id)) continue;

        const matches = selected.filter((item) =>
          group.items.includes(item)
        ).length;
        if (matches === 3) {
          isOneAway = true;
          break;
        }
      }

      if (isOneAway) {
        // One away - deduct a mistake but show encouragement
        const newMistakes = mistakesRemaining - 1;
        setMistakesRemaining(newMistakes);
        setMessage("One away! 🤏 Try again!");
        setSelectedItems([]);

        if (newMistakes === 0) {
          setGameOver(true);
          setMessage("Game over! Want to see the answers?");
        } else {
          setTimeout(() => setMessage(""), 2000);
        }
      } else {
        // Wrong answer - deduct a mistake
        const newMistakes = mistakesRemaining - 1;
        setMistakesRemaining(newMistakes);
        setMessage("Not quite... Try again!");
        setSelectedItems([]);

        if (newMistakes === 0) {
          setGameOver(true);
          setMessage("Game over! Want to see the answers?");
        } else {
          setTimeout(() => setMessage(""), 2000);
        }
      }
    }
  };

  const handleShuffle = () => {
    // Only shuffle unsolved items
    const solvedItems = solvedGroups.flatMap((g) => g.items);
    const unsolvedItems = items.filter((item) => !solvedItems.includes(item));
    const shuffled = shuffleArray(unsolvedItems);

    // Keep solved items at the top in their groups
    const newItems = [...solvedItems, ...shuffled];
    setItems(newItems);
  };

  const revealAnswers = () => {
    setSolvedGroups(connectionsGroups);
    setGameOver(true);
    setMessage("Here are all the answers!");
  };

  const getDifficultyClass = (difficulty) => {
    const classMap = {
      easy: "difficulty-easy",
      medium: "difficulty-medium",
      hard: "difficulty-hard",
      tricky: "difficulty-tricky",
    };
    return classMap[difficulty] || "difficulty-easy";
  };

  const remainingItems = items.filter(
    (item) => !solvedGroups.some((group) => group.items.includes(item))
  );

  return (
    <div className="connections-game">
      <div className="game-intro">
        <p>
          Have fun with these puzzles, don't get any wrong and lower down the
          volume
        </p>
        <p>I'll know and then return your gifts</p>
      </div>

      {/* Solved Groups */}
      {solvedGroups.map((group) => (
        <div
          key={group.id}
          className={`solved-group ${getDifficultyClass(group.difficulty)}`}
        >
          <h3 className="group-category">{group.category}</h3>
          <div className="group-items">{group.items.join(", ")}</div>
        </div>
      ))}

      {/* Game Grid */}
      {!gameWon && (
        <div className="connections-grid">
          {remainingItems.map((item, index) => (
            <button
              key={index}
              className={`connection-item ${
                selectedItems.includes(item) ? "selected" : ""
              }`}
              onClick={() => handleItemClick(item)}
              disabled={gameOver}
            >
              {item}
            </button>
          ))}
        </div>
      )}

      {/* Mistakes Remaining */}
      <div className="mistakes-container">
        <span>Mistakes remaining:</span>
        <div className="mistakes-dots">
          {[...Array(4)].map((_, i) => (
            <span
              key={i}
              className={`mistake-dot ${i < mistakesRemaining ? "active" : ""}`}
            />
          ))}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`message ${
            gameWon
              ? "message-success"
              : gameOver
              ? "message-error"
              : "message-info"
          }`}
        >
          {message}
        </div>
      )}

      {/* Controls */}
      <div className="game-controls">
        {!gameWon && (
          <button
            className="control-button submit-button"
            onClick={handleSubmit}
            disabled={selectedItems.length !== 4 || gameOver}
          >
            Submit
          </button>
        )}
        <button
          className="control-button"
          onClick={handleShuffle}
          disabled={gameOver || gameWon || remainingItems.length === 0}
        >
          Shuffle
        </button>
        <button className="control-button" onClick={resetGame}>
          Reset Puzzle
        </button>
        {gameOver && !gameWon && (
          <button
            className="control-button reveal-button"
            onClick={revealAnswers}
          >
            Reveal Answers
          </button>
        )}
      </div>

      {/* Victory Audio */}
      <audio ref={audioRef} preload="auto" style={{ display: "none" }}>
        {/* Add your CSGO music file to public/victory-music.mp3 */}
        <source src="/victory-music.mp3" type="audio/mpeg" />
      </audio>
    </div>
  );
}

export default ConnectionsGame;
